import React from 'react';
import PropTypes from 'prop-types';
import { BOAT_TYPES } from '../../../utils/boatTypes';
import {
  validateRowerSideForSeat,
  getExpectedSideForSeat,
  parseRiggingConfig,
} from '../../../utils/riggingValidation';

const EnhancedSeatSlot = ({
  crew,
  seat_number,
  rowerInSeat,
  isDropTarget,
  onDragOver,
  onDragEnter,
  onDragLeave,
  onDrop,
  onRemoveRower,
  onDragStart,
  showValidation = true,
}) => {
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onDrop(e, seat_number);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onDragEnter(e, seat_number);
  };

  const handleRemove = () => {
    if (rowerInSeat) {
      // Get the correct ID whether the rower data is wrapped in .user or not
      const rowerId = rowerInSeat.user?.id || rowerInSeat.id;
      onRemoveRower(rowerId);
    }
  };

  const handleRowerDragStart = (e) => {
    if (rowerInSeat && onDragStart) {
      // Pass the actual rower data, whether it's wrapped in .user or not
      const rowerData = rowerInSeat.user || rowerInSeat;
      onDragStart(e, rowerData);
    }
  };

  // Determine seat display info
  const getSeatInfo = () => {
    if (seat_number === 0) {
      return {
        seatNumber: 'Cox',
        displayText: 'Coxswain',
        expectedSide: null,
        position: 'cox',
      };
    }

    // Get expected side from rigging configuration
    let expectedSide = null;

    // For strict side preference crews, rigging config is required
    if (crew.side_preference_strict) {
      if (!crew.rigging_config) {
        console.error(`Crew ${crew.id} has strict side preference but no rigging config`);
        return null; // Return null to indicate error state
      }

      const riggingConfig = parseRiggingConfig(crew.rigging_config);
      if (!riggingConfig || Object.keys(riggingConfig).length === 0) {
        console.error(`Crew ${crew.id} has strict side preference but empty rigging config`);
        return null; // Return null to indicate error state
      }

      expectedSide = getExpectedSideForSeat(riggingConfig, seat_number);
      if (!expectedSide) {
        console.error(
          `Crew ${crew.id} has strict side preference but no expected side for seat ${seat_number}`
        );
        return null; // Return null to indicate error state
      }
    } else {
      // For non-strict crews, use rigging config if available, otherwise fallback
      if (crew.rigging_config) {
        const riggingConfig = parseRiggingConfig(crew.rigging_config);
        expectedSide = getExpectedSideForSeat(riggingConfig, seat_number);
      }

      // Fallback to alternating pattern only for non-strict crews
      if (!expectedSide) {
        expectedSide = seat_number % 2 === 1 ? 'starboard' : 'port';
      }
    }

    // Check for rigging error
    const hasRiggingError = crew.side_preference_strict && expectedSide === null;

    if (seat_number === 1) {
      return {
        seatNumber: '1',
        displayText: hasRiggingError
          ? 'Bow (Seat 1) - Rigging Error'
          : `Bow (Seat 1) - ${expectedSide}`,
        expectedSide,
        position: 'bow',
        hasRiggingError,
      };
    }

    return {
      seatNumber: seat_number.toString(),
      displayText: hasRiggingError
        ? `Seat ${seat_number} - Rigging Error`
        : `Seat ${seat_number} - ${expectedSide}`,
      expectedSide,
      position: 'rower',
      hasRiggingError,
    };
  };

  const getSideValidation = () => {
    if (!rowerInSeat || !showValidation || seat_number === 0) return null;

    // Skip validation for non-strict side preference crews
    if (!crew.side_preference_strict) return null;

    const riggingConfig = parseRiggingConfig(crew.rigging_config);
    const expectedSide = getExpectedSideForSeat(riggingConfig, seat_number);

    if (!expectedSide) return null;

    const validation = validateRowerSideForSeat(rowerInSeat.user || rowerInSeat, expectedSide);

    if (!validation.isValid) {
      return {
        type: 'warning',
        message: validation.message,
      };
    }

    return {
      type: 'success',
      message: validation.message || 'Side preference match',
    };
  };

  const getAvailabilityInfo = () => {
    if (!rowerInSeat) return null;

    // Get availability status from the rower data
    // Check both direct properties and user property (for different data structures)
    const availabilityStatus =
      rowerInSeat.availability_status || rowerInSeat.user?.availability_status;
    const availabilityDetails =
      rowerInSeat.availability_details || rowerInSeat.user?.availability_details;

    if (!availabilityStatus) return null;

    switch (availabilityStatus) {
      case 'fully_available':
        return {
          icon: '✅',
          text: 'Fully Available',
          className: 'text-green-600',
        };
      case 'partly_available':
        const availablePeriods = availabilityDetails?.conflicts?.[0]?.available_periods;
        const periodsText = availablePeriods
          ? availablePeriods.map((p) => `${p.start_time}-${p.end_time}`).join(', ')
          : `${availabilityDetails?.available_minutes || 0} min`;

        return {
          icon: '⚠️',
          text: `Partly Available (${periodsText})`,
          className: 'text-amber-600',
        };
      case 'not_available': {
        if (availabilityDetails?.declared_unavailable_for_session) {
          return {
            icon: '⊘',
            text: 'Unavailable (entered)',
            className: 'text-rose-600',
          };
        }
        return {
          icon: '❌',
          text: 'Not Available',
          className: 'text-red-600',
        };
      }
      case 'already_assigned':
        return {
          icon: '🚫',
          text: 'Already Assigned',
          className: 'text-gray-600',
        };
      case 'conflict':
        const conflictSession = availabilityDetails?.conflicts?.[0];
        const conflictText = conflictSession
          ? `Conflict: ${conflictSession.session_name}`
          : 'Conflicting Session';

        return {
          icon: '⚠️',
          text: conflictText,
          className: 'text-yellow-600',
        };
      default:
        return null;
    }
  };

  const seatInfo = getSeatInfo();
  const { seatNumber, displayText, hasRiggingError } = seatInfo || {
    seatNumber: seat_number.toString(),
    displayText: seat_number === 0 ? 'Coxswain' : `Seat ${seat_number}`,
    hasRiggingError: false,
  };
  const sideValidation = getSideValidation();
  const availabilityInfo = getAvailabilityInfo();

  // Get expected side from rigging configuration
  const getExpectedSideFromRigging = () => {
    if (!crew.rigging_config) return null;

    const riggingConfig = parseRiggingConfig(crew.rigging_config);
    const expectedSide = getExpectedSideForSeat(riggingConfig, seat_number);

    return expectedSide;
  };

  const expectedSideFromRigging = getExpectedSideFromRigging();

  return (
    <div
      className={`
                relative min-h-[80px] rounded-lg border-2 border-dashed p-4 transition-all duration-200
                ${
                  isDropTarget
                    ? 'scale-105 border-blue-400 bg-blue-50'
                    : rowerInSeat
                      ? 'border-gray-200 bg-white shadow-sm'
                      : 'border-gray-300 bg-gray-50 hover:border-gray-400'
                }
                ${sideValidation?.type === 'error' ? 'ring-2 ring-red-200' : ''}
                ${sideValidation?.type === 'warning' ? 'ring-2 ring-amber-200' : ''}
                ${sideValidation?.type === 'success' ? 'ring-2 ring-green-200' : ''}
                ${hasRiggingError ? 'bg-red-50 ring-2 ring-red-500' : ''}
            `}
      onDragOver={onDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={onDragLeave}
      onDrop={handleDrop}
    >
      {/* Seat Label */}
      <div className="mb-2 flex items-center justify-between">
        <div className="text-sm font-medium text-gray-700">{seatNumber}</div>
        {expectedSideFromRigging && (
          <div
            className={`rounded px-2 py-1 text-xs ${
              expectedSideFromRigging === 'port'
                ? 'bg-red-100 text-red-700'
                : 'bg-green-100 text-green-700'
            }`}
          >
            {expectedSideFromRigging}
          </div>
        )}
      </div>

      {rowerInSeat ? (
        <div draggable={true} onDragStart={handleRowerDragStart} className="cursor-move">
          <div className="flex items-center justify-between">
            <div className="flex min-w-0 flex-1 items-center">
              <div className="mr-2 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-blue-500 text-xs font-semibold text-white">
                {(rowerInSeat.user?.first_name || rowerInSeat.first_name)?.[0]}
                {(rowerInSeat.user?.last_name || rowerInSeat.last_name)?.[0]}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-gray-900">
                  {rowerInSeat.user?.first_name || rowerInSeat.first_name}{' '}
                  {rowerInSeat.user?.last_name || rowerInSeat.last_name}
                </div>
                {(rowerInSeat.user?.preferred_side || rowerInSeat.preferred_side) && (
                  <div className="text-xs text-gray-500">
                    Prefers {rowerInSeat.user?.preferred_side || rowerInSeat.preferred_side}
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={handleRemove}
              className="ml-2 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold text-gray-400 transition-colors hover:text-red-500"
              title="Remove rower"
            >
              ×
            </button>
          </div>

          {/* Side validation indicator */}
          {sideValidation && (
            <div
              className={`mt-2 flex items-center gap-1 text-xs ${
                sideValidation.type === 'error'
                  ? 'text-red-600'
                  : sideValidation.type === 'warning'
                    ? 'text-amber-600'
                    : 'text-green-600'
              }`}
            >
              <span>
                {sideValidation.type === 'error'
                  ? '❌'
                  : sideValidation.type === 'warning'
                    ? '⚠️'
                    : '✅'}
              </span>
              {sideValidation.message}
            </div>
          )}

          {/* Availability information indicator */}
          {availabilityInfo && (
            <div className={`mt-2 flex items-center gap-1 text-xs ${availabilityInfo.className}`}>
              <span>{availabilityInfo.icon}</span>
              <span>{availabilityInfo.text}</span>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center text-gray-400">
          <div className="mb-1 text-xs">{displayText}</div>
          {hasRiggingError && (
            <div className="mb-1 text-xs text-red-600">⚠️ Rigging config required</div>
          )}
          <div className="text-xs">Drop rower here</div>
        </div>
      )}
    </div>
  );
};

EnhancedSeatSlot.propTypes = {
  crew: PropTypes.object.isRequired,
  seat_number: PropTypes.number.isRequired,
  rowerInSeat: PropTypes.object,
  isDropTarget: PropTypes.bool,
  onDragOver: PropTypes.func.isRequired,
  onDragEnter: PropTypes.func.isRequired,
  onDragLeave: PropTypes.func.isRequired,
  onDrop: PropTypes.func.isRequired,
  onRemoveRower: PropTypes.func.isRequired,
  onDragStart: PropTypes.func,
  showValidation: PropTypes.bool,
};

export default EnhancedSeatSlot;
